import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Form} from 'formik';
import UnitBlock from '../UnitBlock';
import FieldRadiobutton from '../../../../common/components/FieldRadiobutton';
import FieldInput from '../../../../common/components/FieldInput';
import ButtonCommon from "../../../../common/components/ButtonCommon";
import FieldSelect from "../../../../common/components/FieldSelect";
import FieldCheckbox from "../../../../common/components/FieldCheckbox";
import ButtonsSubmitCancel from "../ButtonsSubmitCancel";
import {useCreateShumaContext} from '../CreateShumaContext';
import {useDecisionTreeShumaTypesOptionsContext} from "../../../../common/components/DecisionTreeOptions";
import {limitIntegerValueLength} from '../../../../common/utils/form/limitValueLength';
import {decisionTree} from '../../constants/decisionTree';
import styles from './styles.module.scss';


const FormCreateShumaView = ({
                               values,
                               nextPagePath,
                               resetDecisionTree,
                               resetPreviousSteps,
                             }) => {
  const [showBankData, setShowBankData] = useState(false);
  const {
    options,
    decisionTreeOptions
  } = useCreateShumaContext();
  const {
    reasons,
    banks,
    propertyTypes,
    loanTypes,
    loanSubTypes,
    shumaMazminIdentities
  } = decisionTreeOptions;
  const {
    shumaTypes,
    updateShumaTypes,
  } = useDecisionTreeShumaTypesOptionsContext();

  useEffect(() => {
    updateShumaTypes(values.shumaReasonID)
  }, []);

  const showSection = (fieldId) => {
    switch (fieldId) {
      case 'propertyTypeID-rightsEstimation':
        return values.shumaReasonID === decisionTree.rightsEstimation;
      case 'bankID':
        return values.shumaReasonID === decisionTree.bankSecure;
      case 'shumaTypeID':
        return (
          values.shumaReasonID === decisionTree.rightsEstimation ||
          values.shumaReasonID === decisionTree.bankSecure
        );
      case 'propertyTypeID':
        return Boolean(values.bankID) && Boolean(values.shumaTypeID);
      case 'loanTypeID':
        return Boolean(values.bankID)
          && Boolean(values.shumaTypeID)
          && Boolean(values.propertyTypeID);
      case 'loanText':
        if (values.loanTypeID) {
          return values.loanTypeID?.value?.toString() === decisionTree.loanTypeID.another;
        } else return null;
      case 'otherPropertyTypeID':
        if (values.loanTypeID) {
          return values.loanTypeID?.value?.toString() === decisionTree.loanTypeID.other;
        } else return null;
      default:
        return true;
    }
  };

  const reason_rightsEstimation = reasons[0]?.value?.toString() || '1';
  const reason_bankSecure = reasons[1]?.value?.toString() || '2';

  return (
    <Form className={styles.formContainer}>
      <div>
        {/**  DECISION TREE  */}
        <UnitBlock>
          <div className={styles.shumaReasonContainer}>
            <FieldRadiobutton
              fieldId={'shumaReasonID'}
              value={reason_bankSecure}
              label={'בטוחה בנקאית'}
              icon={<i className={`la la-bank ${styles.radioIcon}`}/>}
              onChange={resetDecisionTree}
              disabled={options.disabled.includes('shumaReasonID')}
            />
            <FieldRadiobutton
              fieldId={'shumaReasonID'}
              value={reason_rightsEstimation}
              label={'מטרה אחרת'}
              icon={<i className={`la la-home ${styles.radioIcon}`}/>}
              onChange={resetDecisionTree}
              disabled={options.disabled.includes('shumaReasonID')}
            />
          </div>
        </UnitBlock>
        {showSection('shumaTypeID') && (
          <UnitBlock>
            <FieldSelect
              fieldId="shumaTypeID"
              options={shumaTypes}
              label={'סוג דוח'}
              mandatoryMark={true}
              containerStyles={styles.halfSize}
              onChange={({value}) => resetPreviousSteps('shumaTypeID', value)}
              disabled={options.disabled.includes('shumaTypeID')}
            />
          </UnitBlock>
        )}
        {showSection('propertyTypeID-rightsEstimation') && (
          <UnitBlock>
            <FieldSelect
              fieldId="propertyTypeID"
              options={propertyTypes}
              label={'סוג נכס'}
              mandatoryMark={true}
              containerStyles={styles.halfSize}
              onChange={({value}) => resetPreviousSteps('propertyTypeID', value)}
              disabled={options.disabled.includes('propertyTypeID')}
            />
            <FieldCheckbox
              fieldId={'underConstruction'}
              label="נכס בבנייה"
              value={values.underConstruction}
              containerStyles={styles.underConstruction}
              disabled={true}
            />
          </UnitBlock>
        )}
        {showSection('bankID') && (
          <>
            <UnitBlock>
              <div className={styles.bankDataContainer}>
                <FieldSelect
                  fieldId="bankID"
                  options={banks}
                  label={'בנק'}
                  mandatoryMark={true}
                  containerStyles={styles.halfSize}
                  onChange={({value}) => resetPreviousSteps('bankID', value)}
                />
                {Boolean(values?.bankID?.value) && (
                  <ButtonCommon
                    label={showBankData
                      ? 'הסתר מידע מורחב'
                      : 'הצג מידע מורחב'
                    }
                    onClick={() => setShowBankData(!showBankData)}
                    customContainerStyles={styles.showBankDataButton}
                  />
                )}
              </div>
            </UnitBlock>
            {(showBankData && Boolean(values?.bankID?.value)) && (
              <UnitBlock>
                <div className={styles.row}>
                  <FieldInput
                    fieldId={'bankBranchNumber'}
                    value={values.bankBranchNumber}
                    onChange={limitIntegerValueLength(32)}
                    label={'מספר סניף'}
                    containerCustomStyles={styles.halfSize}
                    data-testid="input-bankBranchNumber"
                  />
                  <FieldInput
                    fieldId={'bankBranchName'}
                    value={values.bankBranchName}
                    label={'שם סניף'}
                    containerCustomStyles={styles.halfSize}
                    data-testid="input-bankBranchName"
                  />
                </div>
                <div className={styles.row}>
                  <FieldInput
                    fieldId={'bankClerkFullName'}
                    value={values.bankClerkFullName}
                    label={'שם פקיד'}
                    containerCustomStyles={styles.halfSize}
                    data-testid="input-bankClerkFullName"
                  />
                  <FieldInput
                    fieldId={'bankEmail'}
                    value={values.bankEmail}
                    label={'מייל'}
                    containerCustomStyles={styles.halfSize}
                    data-testid="input-bankEmail"
                  />
                </div>
                <div className={styles.row}>
                  <FieldInput
                    fieldId={'bankRequestID'}
                    value={values.bankRequestID}
                    label={'מס\' בקשה/הלוואה/בטחון'}
                    containerCustomStyles={styles.halfSize}
                    data-testid="input-bankRequestID"
                  />
                  <FieldInput
                    fieldId={'bankBuildType'}
                    value={values.bankBuildType}
                    label={'צפיפות לדונם'}
                    containerCustomStyles={styles.halfSize}
                    data-testid="input-bankBuildType"
                  />
                </div>
              </UnitBlock>
            )}
            {showSection('propertyTypeID') && (
              <UnitBlock>
                <FieldSelect
                  fieldId="propertyTypeID"
                  options={propertyTypes}
                  label={'סוג נכס'}
                  mandatoryMark={true}
                  containerStyles={styles.halfSize}
                  onChange={({value}) => resetPreviousSteps('propertyTypeID', value)}
                  disabled={options.disabled.includes('propertyTypeID')}
                />
                <FieldCheckbox
                  fieldId={'underConstruction'}
                  label="נכס בבנייה"
                  value={values.underConstruction}
                  containerStyles={styles.underConstruction}
                  disabled={true}
                />
              </UnitBlock>
            )}
            {showSection('loanTypeID') && (
              <>
                <UnitBlock>
                  <div className={styles.row}>
                    <FieldSelect
                      fieldId="loanTypeID"
                      options={loanTypes}
                      label={'מטרת ההלוואה'}
                      containerStyles={styles.halfSize}
                      onChange={({value}) => resetPreviousSteps('loanTypeID', value)}
                    />
                    {values.loanTypeID?.value && (
                      <FieldSelect
                        fieldId="loanSubTypeID"
                        options={loanSubTypes}
                        label={'סוג הלוואה'}
                        containerStyles={styles.halfSize}
                      />
                    )}
                  </div>
                </UnitBlock>
              </>
            )}
            {showSection('otherPropertyTypeID') && (
              <UnitBlock>
                <FieldSelect
                  fieldId="otherPropertyTypeID"
                  options={propertyTypes}
                  mandatoryMark={true}
                  label={'סוג נכס אחר'}
                  containerStyles={styles.halfSize}
                />
              </UnitBlock>
            )}
            {showSection('loanText') && (
              <UnitBlock>
                <FieldInput
                  fieldId={`loanText`}
                  mandatoryMark={true}
                  label={'פירוט מטרת ההלוואה'}
                  value={values.loanText}
                />
              </UnitBlock>
            )}
          </>
        )}
        <UnitBlock>
          <FieldSelect
            fieldId="shumaMazminIdentityID"
            options={shumaMazminIdentities}
            mandatoryMark={true}
            label={'זהות מזמין השומה'}
            containerStyles={styles.halfSize}
          />
        </UnitBlock>
        <UnitBlock>
          <FieldCheckbox
            fieldId="inspectionAtRisk"
            value={values.inspectionAtRisk}
            label={'דוח בסיכון'}
            containerStyles={styles.inspectionAtRisk}
          />
          {values.inspectionAtRisk && (
            <FieldInput
              fieldId="inspectionAtRiskDesc"
              value={values.inspectionAtRiskDesc}
              label={'פירוט הסיכון'}
            />
          )}
        </UnitBlock>
      </div>
      <div className={styles.buttons}>
        <ButtonsSubmitCancel
          nextPagePath={nextPagePath}
        />
      </div>
    </Form>
  );
};

FormCreateShumaView.propTypes = {
  values: PropTypes.object,
  isValid: PropTypes.bool,
  resetDecisionTree: PropTypes.func,
  resetPreviousSteps: PropTypes.func,
  handleBankPropertiesSelect: PropTypes.func,
  step13Form: PropTypes.bool
};

export default FormCreateShumaView;
